// One script-aware palette serves both DOM lettering and canvas inscriptions.
// Families are loaded only when their script appears. Questions never enter a
// font-provider URL. The text argument is used locally by the FontFaceSet API.
const FAMILIES = {
  latin:'La Belle Aurore', extended:'Caveat', vietnamese:'Patrick Hand',
  han:'LXGW WenKai TC', japanese:'Yomogi', korean:'Nanum Pen Script',
  cyrillic:'Caveat', greek:'Mynerve', arabic:'Aref Ruqaa', hebrew:'Gveret Levin',
  devanagari:'Kalam', thai:'Sriracha',
};
const SCRIPTS = [
  ['japanese',/[\p{Script=Hiragana}\p{Script=Katakana}]/u],
  ['korean',/\p{Script=Hangul}/u], ['han',/\p{Script=Han}/u],
  ['cyrillic',/\p{Script=Cyrillic}/u], ['greek',/\p{Script=Greek}/u],
  ['arabic',/\p{Script=Arabic}/u], ['hebrew',/\p{Script=Hebrew}/u],
  ['devanagari',/\p{Script=Devanagari}/u], ['thai',/\p{Script=Thai}/u],
];
const VIETNAMESE=/[ĂăĐđĨĩŨũƠơƯưẠ-ỹ]/u;

export function handwritingProfile(value='', language='') {
  const text=String(value).normalize('NFC'),code=String(language).toLowerCase();
  const scripts=SCRIPTS.filter(([,pattern])=>pattern.test(text)).map(([key])=>key);
  // Han characters on a Japanese page use Japanese letterforms, including
  // a question containing no kana. The actual text wins over an unrelated UI language.
  if(scripts.includes('han')&&(scripts.includes('japanese')||/^ja(?:-|$)/.test(code)))scripts.splice(scripts.indexOf('han'),1,'japanese');
  const latin=VIETNAMESE.test(text)||/^vi(?:-|$)/.test(code)?'vietnamese':/[\u0100-\u024f]/u.test(text)?'extended':'latin';
  const families=[...new Set([...scripts.map(script=>FAMILIES[script]),FAMILIES[latin]])];
  const firstLetter=text.match(/\p{Letter}/u)?.[0]||'';
  const direction=/[\p{Script=Arabic}\p{Script=Hebrew}]/u.test(firstLetter)?'rtl':'ltr';
  return {families,family:families.map(name=>`"${name}"`).join(', ')+', cursive',direction};
}

export const handwritingFont=(size,text,language)=>`${size}px ${handwritingProfile(text,language).family}`;
export const handwritingGraphemes=text=>typeof Intl.Segmenter==='function'
  ? [...new Intl.Segmenter(undefined,{granularity:'grapheme'}).segment(String(text))].map(item=>item.segment)
  : Array.from(String(text));

export function wrapHandwriting(context,text,width) {
  const lines=[];
  for(const paragraph of String(text).split(/\r?\n/u)){
    let line='';
    for(const token of paragraph.match(/\S+\s*/gu)||[]){
      if(line&&context.measureText(line+token.trimEnd()).width>width){lines.push(line.trimEnd());line='';}
      for(const glyph of handwritingGraphemes(token)){
        if(line&&context.measureText(line+glyph).width>width){lines.push(line.trimEnd());line='';}
        line+=glyph;
      }
    }
    lines.push(line.trimEnd());
  }
  return lines;
}

export function paintHandwriting(context,text,x,y,{width,size,lineHeight=size*1.35,maxLines=Infinity,language=''}={}) {
  context.save();
  const profile=handwritingProfile(text,language);
  context.font=handwritingFont(size,text,language);context.direction=profile.direction;
  context.textAlign=profile.direction==='rtl'?'right':'left';
  const lines=wrapHandwriting(context,text,width);
  lines.slice(0,maxLines).forEach((line,index)=>{
    let value=line;
    if(index===maxLines-1&&lines.length>maxLines){
      const glyphs=handwritingGraphemes(value);
      while(glyphs.length&&context.measureText(glyphs.join('')+'…').width>width)glyphs.pop();
      value=glyphs.join('').trimEnd()+'…';
    }
    context.fillText(value,x+(profile.direction==='rtl'?width:0),y+index*lineHeight);
  });
  context.restore();
}

const stylesheets=new Map();
function ensureFamily(family,doc) {
  if([...doc.fonts].some(face=>face.family.replace(/["']/g,'')===family))return Promise.resolve();
  if(stylesheets.has(family))return stylesheets.get(family);
  const promise=new Promise((resolve,reject)=>{
    const link=doc.createElement('link');link.rel='stylesheet';
    link.href=`https://fonts.googleapis.com/css2?family=${encodeURIComponent(family).replace(/%20/g,'+')}&display=swap`;
    const timer=setTimeout(()=>finish(new Error('Font stylesheet timed out')),12000);
    function finish(error){clearTimeout(timer);link.onload=null;link.onerror=null;if(error){link.remove();reject(error);}else resolve();}
    link.onload=()=>finish();link.onerror=()=>finish(new Error('Font stylesheet unavailable'));
    doc.head.appendChild(link);
  });
  stylesheets.set(family,promise);promise.catch(()=>stylesheets.delete(family));
  return promise;
}

export async function ensureHandwritingFonts(text,language='',doc=typeof document==='undefined'?null:document) {
  if(!doc?.fonts||!text)return;
  const {families}=handwritingProfile(text,language);
  await Promise.allSettled(families.map(async family=>{
    await ensureFamily(family,doc);
    await doc.fonts.load(`48px "${family}"`,String(text));
  }));
}

function applyHandwriting(element,binding) {
  const value=binding.value, text=typeof value==='string'?value:value?.text||element.textContent||'';
  const language=typeof value==='object'?value?.language||'':'';
  const key=JSON.stringify([text,language]);
  if(element.dataset.handwriting===key)return;
  element.dataset.handwriting=key;
  element.style.fontFamily=handwritingProfile(text,language).family;
  element.dir='auto';
  ensureHandwritingFonts(text,language,element.ownerDocument);
}
export const handwritingDirective={mounted:applyHandwriting,updated:applyHandwriting};
